import backIcon from '@iconify/icons-material-symbols/arrow-back-rounded';
import closeIcon from '@iconify/icons-material-symbols/close-rounded';
import expandMoreIcon from '@iconify/icons-material-symbols/expand-more-rounded';
import historyIcon from '@iconify/icons-material-symbols/history-rounded';
import sendIcon from '@iconify/icons-material-symbols/send-rounded';
import supportAgentIcon from '@iconify/icons-material-symbols/support-agent-rounded';
import { Icon } from '@iconify/react';
import { LoadingButton } from '@mui/lab';
import { visuallyHidden } from '@mui/utils';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
    CHAT_CONSENT_VERSION,
    CHAT_STATUS_SEARCHING_DOCS,
    ChatNoteContext,
    ChatScopeLesson,
    deleteChatSessionsOnServer,
    fetchChatSession,
    fetchChatSessions,
    isChatCallError,
    isChatNote,
    prefetchChatScope,
    rateChatMessage,
    recordChatConsentOnServer,
    streamChatMessage,
} from 'external/firebase/controllers/chat';
import useAuth from 'hooks/useAuth';
import useLocales from 'hooks/useLocales';
import { APP_ROLES, hasRole } from 'utils/roles';

import ChatHandoffDialog from './ChatHandoffDialog';
import ChatMessageRating from './ChatMessageRating';
import ChatRichText from './ChatRichText';
import ChatSessionList from './ChatSessionList';
import ChatTestAsBar from './ChatTestAsBar';
import {
    applyServerTranscript,
    capSessions,
    ChatSession,
    clearChatConsent,
    ensureChatKeysRenamed,
    hasGivenChatConsent,
    loadChatScope,
    loadStore,
    mergeServerSessions,
    recordChatConsent,
    saveChatScope,
    saveStore,
    scopeModules,
    setMessageRating,
    StoredMessage,
    upsertMessage,
} from './chatSessionStore';
import { quotaLabelKey, quotaTone } from './quotaLabel';

// ----------------------------------------------------------------------

// Matches the function's entitlement cache: a reopen after this re-asks.
const SCOPE_REFRESH_MS = 5 * 60 * 1000;
const SCOPE_MAX_CHIPS = 12;
// Until the first prefetch says otherwise; the function's DAILY_STUDENT_MESSAGE_CAP.
const DEFAULT_DAILY_LIMIT = 40;

// Legacy flat key from before per-session persistence. It only ever held a bare
// session_id (no transcript), so there's nothing to migrate — we drop it on load.
const LEGACY_SESSION_KEY = 'kursuok_chat_session_id';

// Drag-to-resize width, remembered across sessions (not per-uid — it's a
// viewport preference, not user data). Only meaningful at the `sm` breakpoint
// and up; below that the drawer is always full-width (see the Drawer's
// PaperProps) and there's nothing to resize.
const CHAT_WIDTH_KEY = 'cao_chat_width';
const DEFAULT_CHAT_WIDTH = 420;
const MIN_CHAT_WIDTH = 320;
// Keep at least this much of the page visible on the left rather than letting
// the drawer swallow the whole viewport.
const MIN_VISIBLE_PAGE_WIDTH = 480;

const clampChatWidth = (width: number): number => {
    const max = Math.max(MIN_CHAT_WIDTH, window.innerWidth - MIN_VISIBLE_PAGE_WIDTH);
    return Math.min(Math.max(width, MIN_CHAT_WIDTH), max);
};

const loadStoredChatWidth = (): number => {
    ensureChatKeysRenamed();
    try {
        const raw = localStorage.getItem(CHAT_WIDTH_KEY);
        const parsed = raw ? Number(raw) : NaN;
        return Number.isFinite(parsed) ? clampChatWidth(parsed) : DEFAULT_CHAT_WIDTH;
    } catch {
        return DEFAULT_CHAT_WIDTH;
    }
};

interface ChatPanelProps {
    open: boolean;
    onClose: () => void;
}

// crypto.randomUUID needs a secure context, which every real deployment of
// this app is; the fallback just keeps plain-http local dev working too.
const generateSessionId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

const TYPEWRITER_TICK_MS = 20;

// The reveal speeds up the further it falls behind what's already buffered
// (e.g. once the whole answer has arrived while typing is still catching
// up), so a long reply doesn't drag on forever once there's nothing left to
// actually wait for.
const typewriterStepSize = (backlog: number): number => {
    if (backlog > 200) {
        return 10;
    }
    if (backlog > 60) {
        return 4;
    }
    return 1;
};

interface TypewriterHandle {
    // Resolves once `isStreamDone()` is true AND every buffered character has
    // been shown — never earlier, so the caller can't finalize the message
    // mid-animation.
    done: Promise<void>;
    // Stops the reveal immediately, e.g. because the turn failed and the
    // partial text is about to be discarded rather than finished typing out.
    stop: () => void;
}

// Reveals `getTarget()`'s current text a few characters at a time via
// `onUpdate`, on its own steady clock — independent of how the SSE tokens
// actually arrived. Raw token timing makes a poor animation on its own: a
// chunk can be a single word or three whole sentences, so displaying each
// chunk the instant it lands would jump in uneven bursts instead of reading
// as one smooth "self-writing" reply.
const revealTextOverTime = (
    getTarget: () => string,
    isStreamDone: () => boolean,
    onUpdate: (shown: string) => void,
    isUnmounted: () => boolean,
): TypewriterHandle => {
    let shown = '';
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = (): void => {
        if (timer !== null) {
            clearInterval(timer);
            timer = null;
        }
    };
    const done = new Promise<void>(resolve => {
        const tick = (): void => {
            const target = getTarget();
            const backlog = target.length - shown.length;
            if (backlog > 0) {
                shown = target.slice(0, shown.length + typewriterStepSize(backlog));
                if (!isUnmounted()) {
                    onUpdate(shown);
                }
            }
            if (backlog <= 0 && isStreamDone()) {
                stop();
                resolve();
            }
        };
        timer = setInterval(tick, TYPEWRITER_TICK_MS);
    });
    return { done, stop };
};

// ----------------------------------------------------------------------

const ChatPanel: React.FunctionComponent<ChatPanelProps> = ({ open, onClose }) => {
    const { translate, currentLang } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const { user, isInitialized, profile } = useAuth();
    const uid = user?.uid;
    // The function enforces the same rule; a persona only narrows what a super-admin sees.
    const canTestAs = hasRole(profile?.group, APP_ROLES.SUPER_ADMIN);
    const [testAs, setTestAs] = useState<string[] | null>(null);

    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    // The in-flight assistant reply, growing token by token as it streams in —
    // the "self-writing" animation. null while nothing is streaming; '' the
    // instant a turn starts (before the first token lands), which is what
    // keeps the "thinking" indicator up until there's real text to show.
    // Never touches `sessions`/localStorage — only a complete turn does, on
    // the stream's `done` event, same "no half-turns persisted" rule the old
    // buffered flow already followed.
    const [streamingText, setStreamingText] = useState<string | null>(null);
    // True between the service's "searching_docs" status and the block's first
    // text: the refusal is already on screen and the official-documentation
    // search behind it takes several seconds.
    const [searchingDocs, setSearchingDocs] = useState(false);
    // What the caller can ask about, shown under a new chat's greeting: the
    // cached copy at once, then the server's on each prefetch.
    const [scopeLessons, setScopeLessons] = useState<ChatScopeLesson[]>([]);
    const [topicsOpen, setTopicsOpen] = useState(false);
    // Messages left today (students only — null for staff, who have no cap).
    const [remainingToday, setRemainingToday] = useState<number | null>(null);
    const [dailyLimit, setDailyLimit] = useState(DEFAULT_DAILY_LIMIT);
    // A "pastaba" note doesn't count against the cap, so it can still be sent at 0.
    const outOfMessages = remainingToday === 0;
    const scopeKeyRef = useRef<string | null>(null);
    const scopeFetchedAtRef = useRef(0);
    const syncedKeyRef = useRef<string | null>(null);
    const syncedAtRef = useRef(0);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [showSessions, setShowSessions] = useState(false);
    // Shown before every new chat is minted — see handleRequestNewChat below.
    const [showIntro, setShowIntro] = useState(false);
    const [deleteTargetIds, setDeleteTargetIds] = useState<string[] | null>(null);
    const [width, setWidth] = useState(loadStoredChatWidth);
    // Recorded once per uid, closed by default — see chatSessionStore's
    // hasGivenChatConsent/recordChatConsent and the intro screen below.
    const [hasConsented, setHasConsented] = useState(false);
    // The current intro screen's checkbox state — only meaningful while
    // !hasConsented; reset each time the intro is (re)shown.
    const [consentChecked, setConsentChecked] = useState(false);
    const [savingConsent, setSavingConsent] = useState(false);
    const [handoffOpen, setHandoffOpen] = useState(false);
    // True when the intro was reopened because the server asked for consent
    // mid-conversation: continuing returns to that chat instead of a new one.
    const introResumesChatRef = useRef(false);

    const loadedUidRef = useRef<string | null>(null);
    const primedRef = useRef(false);
    const sendingRef = useRef(false);
    const creatingRef = useRef(false);
    const resizingRef = useRef(false);
    // The scrollable message-list Box (overflowY: 'auto', set on the element
    // below) — see the scroll-to-bottom effect for why its scrollTop is set
    // directly rather than via an anchor element's .scrollIntoView().
    const messagesContainerRef = useRef<HTMLDivElement | null>(null);
    // The in-flight letter-by-letter typewriter — see revealTextOverTime below.
    // Kept at component scope purely so unmount can stop it; only one send is
    // ever in flight at a time (sendingRef guards that), so a single ref is enough.
    const typewriterRef = useRef<TypewriterHandle | null>(null);
    const unmountedRef = useRef(false);

    useEffect(
        () => () => {
            unmountedRef.current = true;
            typewriterRef.current?.stop();
        },
        [],
    );

    const theme = useTheme();
    // The drawer is always full-width below `sm` (see the Drawer's PaperProps
    // below) — nothing to resize there, so the handle isn't rendered at all.
    const isResizable = useMediaQuery(theme.breakpoints.up('sm'));

    // Single source of truth: the active session's transcript is derived, never
    // duplicated into its own state (which would drift from what we persist).
    const messages = useMemo(
        () => sessions.find(session => session.id === activeSessionId)?.messages ?? [],
        [sessions, activeSessionId],
    );
    const activeSession = sessions.find(session => session.id === activeSessionId);
    const modulesInScope = useMemo(() => scopeModules(scopeLessons), [scopeLessons]);
    // Only under a fresh chat's greeting — once the student has asked anything,
    // the list would just push the conversation down.
    const showScope =
        modulesInScope.length > 0 &&
        !loading &&
        !activeSession?.serverMessageCount &&
        messages.length === 1 &&
        messages[0]?.role === 'assistant';

    // What a reply from the server lands on: the state as it is by then, not as
    // it was when the call went out.
    const sessionsRef = useRef(sessions);
    sessionsRef.current = sessions;
    const activeSessionIdRef = useRef(activeSessionId);
    activeSessionIdRef.current = activeSessionId;

    // Durable write: update state and persist, but only under the uid the state
    // currently reflects — so an in-flight user switch can't save A's data as B's.
    const persist = (nextSessions: ChatSession[], nextActive: string | null): void => {
        const capped = capSessions(nextSessions, nextActive);
        setSessions(capped);
        setActiveSessionId(nextActive);
        if (uid && loadedUidRef.current === uid) {
            saveStore(uid, { activeSessionId: nextActive, sessions: capped });
        }
    };

    // Prefetch as soon as the panel mounts (the dashboard has loaded), not when
    // it opens: the function caches the entitlement and wakes the content
    // service while the student is still reading the page. Again on open once
    // the copy is older than SCOPE_REFRESH_MS, and for every test-as persona.
    const scopeKey = uid ? `${uid}${testAs ? `~${testAs.join(',')}` : ''}` : null;
    useEffect(() => {
        if (!scopeKey) {
            return;
        }
        const keyChanged = scopeKeyRef.current !== scopeKey;
        if (keyChanged) {
            scopeKeyRef.current = scopeKey;
            scopeFetchedAtRef.current = 0;
            setScopeLessons(loadChatScope(scopeKey));
        } else if (!open || Date.now() - scopeFetchedAtRef.current < SCOPE_REFRESH_MS) {
            return;
        }
        scopeFetchedAtRef.current = Date.now();
        prefetchChatScope(testAs).then(res => {
            if (isChatCallError(res) || scopeKeyRef.current !== scopeKey) {
                return;
            }
            setScopeLessons(res.lessons);
            saveChatScope(scopeKey, res.lessons);
            setRemainingToday(typeof res.remaining_today === 'number' ? res.remaining_today : null);
            if (typeof res.daily_limit === 'number') {
                setDailyLimit(res.daily_limit);
            }
        });
        // testAs is part of scopeKey.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeKey, open]);

    // Hydrate from storage on mount and whenever the user changes. A session with
    // messages is restored with zero network — this is the "survives refresh" fix.
    useEffect(() => {
        if (!isInitialized) {
            return;
        }
        if (!uid) {
            loadedUidRef.current = null;
            setSessions([]);
            setActiveSessionId(null);
            primedRef.current = false;
            setHasConsented(false);
            return;
        }
        if (loadedUidRef.current === uid) {
            return;
        }
        const store = loadStore(uid);
        loadedUidRef.current = uid;
        setSessions(store.sessions);
        setActiveSessionId(store.activeSessionId);
        // Only a genuinely empty store should auto-create a session on open.
        primedRef.current = store.sessions.length > 0;
        setHasConsented(hasGivenChatConsent(uid, CHAT_CONSENT_VERSION));
        try {
            localStorage.removeItem(LEGACY_SESSION_KEY);
        } catch {
            // ignore — storage disabled
        }
    }, [uid, isInitialized]);

    // Chats kept on the server that this device lacks, or has less of: on open,
    // per identity, again after SCOPE_REFRESH_MS. Never mid-send, which would
    // lose the merge to the send's own snapshot.
    useEffect(() => {
        if (!open || !hasConsented || !scopeKey || loadedUidRef.current !== uid) {
            return;
        }
        if (syncedKeyRef.current === scopeKey && Date.now() - syncedAtRef.current < SCOPE_REFRESH_MS) {
            return;
        }
        syncedKeyRef.current = scopeKey;
        syncedAtRef.current = Date.now();
        fetchChatSessions(testAs).then(res => {
            if (isChatCallError(res) || scopeKeyRef.current !== scopeKey || sendingRef.current) {
                return;
            }
            const merged = mergeServerSessions(sessionsRef.current, res.sessions, translate('chat_greeting'));
            if (merged !== sessionsRef.current) {
                // An empty device opened on the new-chat intro; its chats arrived instead.
                if (sessionsRef.current.length === 0) {
                    setShowIntro(false);
                }
                const newest = [...merged].sort((a, b) => b.updatedAt - a.updatedAt)[0];
                persist(merged, activeSessionIdRef.current ?? newest?.id ?? null);
            }
        });
        // testAs is part of scopeKey; persist and translate are stable enough here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, hasConsented, scopeKey, uid]);

    const pendingTranscriptId = activeSession?.serverMessageCount ? activeSession.id : null;
    useEffect(() => {
        if (!open || !pendingTranscriptId) {
            return;
        }
        setHistoryLoading(true);
        fetchChatSession(pendingTranscriptId, currentLang.value, testAs).then(res => {
            setHistoryLoading(false);
            if (isChatCallError(res)) {
                if (res.code !== 'chat/session-not-found') {
                    enqueueSnackbar(translate('something_went_wrong'), { variant: 'error' });
                    return;
                }
                // Gone from the server (deleted elsewhere, or past 30 days): keep what's here.
                persist(
                    sessionsRef.current.map(session =>
                        session.id === pendingTranscriptId ? { ...session, serverMessageCount: undefined } : session,
                    ),
                    activeSessionIdRef.current,
                );
                return;
            }
            persist(
                applyServerTranscript(
                    sessionsRef.current,
                    pendingTranscriptId,
                    res.messages,
                    translate('chat_greeting'),
                ),
                activeSessionIdRef.current,
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, pendingTranscriptId]);

    // Mint a fresh local chat session with a canned opening greeting — no network
    // call. This used to prime the session with a throwaway "Sveiki" message sent
    // to the backend, a holdover from the old scraper bot, which used to discard
    // a session's first message and reply with its own greeting instead. Our own
    // /v1/chat has no such discard behavior, so priming only spent a real LLM call
    // — and worse, "Sveiki" itself would retrieve unrelated course content and cite
    // it in a "Sources" footer under a message that was never actually answering
    // anything. Only ever called after the intro step's "Continue" — see
    // handleContinueFromIntro. The session_id is generated client-side;
    // cao-content-service creates the matching conversation row lazily on the
    // first real message (see chat.py's get_or_create_conversation).
    const createSession = (): void => {
        if (creatingRef.current) {
            return;
        }
        creatingRef.current = true;
        const now = Date.now();
        const session: ChatSession = {
            id: generateSessionId(),
            title: '',
            createdAt: now,
            updatedAt: now,
            messages: [{ role: 'assistant', content: translate('chat_greeting') }],
        };
        persist([session, ...sessions], session.id);
        creatingRef.current = false;
    };

    // Every path that starts a new chat lands here first — the explicit "New chat"
    // button and the first-ever-open auto-prompt below — rather than minting a
    // session straight away. See handleContinueFromIntro for the actual creation.
    const handleRequestNewChat = (): void => {
        introResumesChatRef.current = false;
        setShowSessions(false);
        setConsentChecked(false);
        setShowIntro(true);
    };

    const handleContinueFromIntro = async (): Promise<void> => {
        if (!hasConsented) {
            if (!consentChecked || !uid) {
                return;
            }
            setSavingConsent(true);
            const saved = await recordChatConsentOnServer();
            setSavingConsent(false);
            if (!saved) {
                enqueueSnackbar(translate('something_went_wrong'), { variant: 'error' });
                return;
            }
            recordChatConsent(uid, CHAT_CONSENT_VERSION);
            setHasConsented(true);
        }
        setShowIntro(false);
        if (introResumesChatRef.current && activeSessionId) {
            introResumesChatRef.current = false;
            return;
        }
        createSession();
    };

    // The note is about the latest real question and its answer in this chat,
    // skipping earlier notes and their confirmations.
    const latestExchange = (): ChatNoteContext | null => {
        for (let i = messages.length - 1; i > 0; i -= 1) {
            const answer = messages[i];
            const question = messages[i - 1];
            if (answer.role === 'assistant' && question.role === 'user' && !isChatNote(question.content)) {
                return { question: question.content, answer: answer.content };
            }
        }
        return null;
    };

    // Only meaningful once there's an existing conversation to return to — the
    // very first ever open has nowhere to go back to but closing the drawer.
    const handleCancelIntro = (): void => {
        setShowIntro(false);
        if (sessions.length > 0) {
            setShowSessions(true);
        }
    };

    // Prompt for the first session the first time the panel opens with an empty
    // store. Keyed on sessions.length so it re-reads state rather than closing over
    // a stale snapshot.
    useEffect(() => {
        if (!open || !isInitialized || !uid || loadedUidRef.current !== uid) {
            return;
        }
        if (sessions.length > 0 || primedRef.current || creatingRef.current) {
            return;
        }
        primedRef.current = true;
        setConsentChecked(false);
        setShowIntro(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isInitialized, uid, sessions.length]);

    // Sets scrollTop directly on the message-list container rather than
    // calling `.scrollIntoView()` on an anchor element. scrollIntoView walks
    // up and scrolls EVERY scrollable ancestor needed to bring the target
    // into view, not just the intended inner container — if any ancestor
    // between the anchor and the message-list Box isn't exactly as tall as
    // expected for a render or two (entirely possible right as the drawer's
    // content mounts, or as the intro/session-list/message-view bodyContent
    // branch swaps), it can end up scrolling the Drawer's own Paper (or, in
    // the worst case, the page behind it) instead of — or in addition to —
    // the message list, which is what produced the "everything hops to the
    // top" glitch. Writing `scrollTop` on a specific element can't escape to
    // an ancestor: there's nothing to accidentally get right or wrong about
    // which container ends up moving. Instant, not smooth-animated, for the
    // same reason as before — streamingText changes on every ~20ms
    // typewriter tick (see revealTextOverTime), and restarting a smoothed
    // scroll animation that often fights itself is what read as jittery
    // "hopping" during a reply. `open` is in the deps so reopening the
    // drawer on an existing conversation lands at the bottom (the latest
    // messages), not wherever the fresh DOM node defaults to (the top) —
    // MUI's Drawer unmounts its content while closed, so the scroll position
    // from before closing isn't there to restore.
    useEffect(() => {
        if (!open) {
            return;
        }
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    }, [open, messages, loading, streamingText]);

    // Keep the bottom of the conversation where it was while the footer grows
    // (the topics opening, the input taking more lines): the list shrinks from
    // below, which would otherwise slide its last lines under the footer.
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (!open || !container || typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        let lastHeight = container.clientHeight;
        const observer = new ResizeObserver(() => {
            const shrunkBy = lastHeight - container.clientHeight;
            lastHeight = container.clientHeight;
            if (shrunkBy > 0) {
                container.scrollTop += shrunkBy;
            }
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [open, showSessions, showIntro]);

    // `preset` is a message sent without going through the input (a module
    // chip's question); whatever the student had typed stays where it was.
    const handleSend = async (preset?: string) => {
        const text = (preset ?? input).trim();
        if (outOfMessages && !isChatNote(text)) {
            return;
        }
        // Guard synchronously (a ref, not `loading` state) so two Enter presses
        // in the same frame can't both slip through and fire duplicate sends.
        if (!text || sendingRef.current || !activeSessionId || historyLoading) {
            return;
        }
        sendingRef.current = true;

        const sid = activeSessionId;
        const base = sessions;
        const userMessage: StoredMessage = { role: 'user', content: text };
        if (preset === undefined) {
            setInput('');
        }
        // Optimistic: show the user bubble immediately, but don't persist a
        // half-turn — only complete turns reach localStorage (see the success path).
        setSessions(upsertMessage(base, sid, userMessage, text));
        setLoading(true);
        setStreamingText('');

        // `assembled` is the full text as it actually arrives over the network;
        // `streamingText` (rendered) instead follows revealTextOverTime's own
        // steady clock — see that function for why the two are kept separate.
        let assembled = '';
        let networkDone = false;
        const typewriter = revealTextOverTime(
            () => assembled,
            () => networkDone,
            setStreamingText,
            () => unmountedRef.current,
        );
        typewriterRef.current = typewriter;
        const res = await streamChatMessage(
            text,
            sid,
            delta => {
                assembled += delta;
                setSearchingDocs(false);
            },
            testAs,
            isChatNote(text) ? latestExchange() : null,
            status => setSearchingDocs(status === CHAT_STATUS_SEARCHING_DOCS),
            currentLang.value,
        );
        networkDone = true;
        setSearchingDocs(false);

        if ('session_id' in res) {
            if (res.remaining_today !== null) {
                setRemainingToday(res.remaining_today);
            }
            if (res.daily_limit !== null) {
                setDailyLimit(res.daily_limit);
            }
            // Let the animation actually finish catching up before the bubble
            // becomes a persisted, static message — otherwise the reply would
            // snap to its full text mid-typewriter.
            await typewriter.done;
            typewriterRef.current = null;
            setStreamingText(null);
            const withUser = upsertMessage(base, sid, userMessage, text);
            const withReply = upsertMessage(withUser, sid, {
                role: 'assistant',
                content: assembled,
                ...(res.message_id ? { messageId: res.message_id } : {}),
            });
            persist(withReply, sid);
        } else {
            // The turn failed — stop the animation immediately rather than
            // typing out text that's about to be discarded anyway.
            typewriter.stop();
            typewriterRef.current = null;
            setStreamingText(null);
            // Undo the failed turn so the user can retry without retyping: drop
            // the orphaned user bubble and put the text back in the input.
            if (preset === undefined) {
                setInput(text);
            }
            let errorKey:
                | 'forbidden_access'
                | 'chat_rate_limited'
                | 'chat_session_conflict'
                | 'chat_not_in_pilot'
                | 'chat_consent_required'
                | 'something_went_wrong' = 'something_went_wrong';
            if (res.code === 'auth/forbidden-access') {
                errorKey = 'forbidden_access';
                setSessions(base);
            } else if (res.code === 'chat/rate-limited') {
                errorKey = 'chat_rate_limited';
                setRemainingToday(0);
                setSessions(base);
            } else if (res.code === 'chat/consent-required') {
                errorKey = 'chat_consent_required';
                setSessions(base);
                if (uid) {
                    clearChatConsent(uid);
                }
                setHasConsented(false);
                setConsentChecked(false);
                introResumesChatRef.current = true;
                setShowIntro(true);
            } else if (res.code === 'chat/not-in-pilot') {
                errorKey = 'chat_not_in_pilot';
                setSessions(base);
            } else if (res.code === 'chat/session-conflict') {
                // This session's id collided with a conversation owned by a
                // different uid server-side (cao-content-service's
                // ConversationOwnershipError — vanishingly rare, but the old
                // id can never work again). Give the session a fresh one so
                // retrying with the restored text actually succeeds.
                errorKey = 'chat_session_conflict';
                const freshId = generateSessionId();
                const withFreshId = base.map(session => (session.id === sid ? { ...session, id: freshId } : session));
                persist(withFreshId, freshId);
            } else {
                setSessions(base);
            }
            enqueueSnackbar(translate(errorKey), { variant: 'error' });
        }
        setLoading(false);
        sendingRef.current = false;
    };

    // Optimistic: the thumb fills at once and is put back if the call fails.
    const handleRate = async (messageId: number, rating: 1 | -1 | null): Promise<void> => {
        if (!activeSessionId) {
            return;
        }
        const before = sessions;
        persist(setMessageRating(sessions, activeSessionId, messageId, rating), activeSessionId);
        const res = await rateChatMessage(messageId, rating, testAs);
        if (isChatCallError(res)) {
            persist(before, activeSessionId);
            enqueueSnackbar(translate('something_went_wrong'), { variant: 'error' });
        }
    };

    const hasAskedSomething = messages.some(message => message.role === 'user' && !isChatNote(message.content));

    const handleTestAsChange = (next: string[] | null): void => {
        setTestAs(next);
        setShowSessions(false);
        if (hasConsented) {
            createSession();
        } else {
            handleRequestNewChat();
        }
    };

    // Today's messages as "sent/limit" inside the input; the full sentence is
    // its tooltip and accessible name.
    const quotaBadge =
        remainingToday === null ? undefined : (
            <InputAdornment position='end'>
                <Tooltip
                    title={
                        outOfMessages
                            ? translate('chat_quota_used_up')
                            : translate(quotaLabelKey(remainingToday, currentLang.value), { n: remainingToday })
                    }
                >
                    <Typography
                        variant='caption'
                        color={`${quotaTone(remainingToday, dailyLimit)}.main`}
                        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', cursor: 'default' }}
                        aria-label={
                            outOfMessages
                                ? translate('chat_quota_used_up')
                                : translate(quotaLabelKey(remainingToday, currentLang.value), { n: remainingToday })
                        }
                    >
                        {Math.max(0, dailyLimit - remainingToday)}/{dailyLimit}
                    </Typography>
                </Tooltip>
            </InputAdornment>
        );

    // The question's wording is what cao-content-service recognises as a module
    // overview request (chat.py _MODULE_OVERVIEW_REQUESTS) — keep the two in step.
    const askAboutModule = (module: string): void => {
        setTopicsOpen(false);
        handleSend(translate('chat_scope_module_question').replace('{module}', module));
    };

    const scopeChips = (
        <Stack direction='row' flexWrap='wrap' gap={0.75} sx={{ mt: 0.5 }}>
            {modulesInScope.slice(0, SCOPE_MAX_CHIPS).map(module => (
                <Chip
                    key={module}
                    label={module}
                    size='small'
                    variant='outlined'
                    clickable
                    disabled={loading || outOfMessages}
                    onClick={() => askAboutModule(module)}
                />
            ))}
            {modulesInScope.length > SCOPE_MAX_CHIPS && (
                <Chip label={`+${modulesInScope.length - SCOPE_MAX_CHIPS}`} size='small' />
            )}
        </Stack>
    );

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    const handleSelectSession = (id: string) => {
        persist(sessions, id);
        setShowSessions(false);
    };

    const handleConfirmDelete = () => {
        if (!deleteTargetIds?.length) {
            return;
        }
        const next = sessions.filter(session => !deleteTargetIds.includes(session.id));
        const nextActive =
            activeSessionId && deleteTargetIds.includes(activeSessionId)
                ? [...next].sort((a, b) => b.updatedAt - a.updatedAt)[0]?.id ?? null
                : activeSessionId;
        persist(next, nextActive);
        if (next.length === 0) {
            // Empty again — let the primer effect re-create a session on reopen.
            primedRef.current = false;
        }
        setDeleteTargetIds(null);
        deleteChatSessionsOnServer(deleteTargetIds, testAs).then(res => {
            if (isChatCallError(res)) {
                enqueueSnackbar(translate('chat_delete_server_failed'), { variant: 'error' });
            }
        });
    };

    // Pointer capture (not window listeners) keeps every subsequent move/up
    // event routed to this element even once the pointer leaves it — the
    // standard way to implement a drag handle without a useEffect + manual
    // window-listener cleanup dance.
    const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>): void => {
        event.preventDefault();
        resizingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleResizeMove = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!resizingRef.current) {
            return;
        }
        // Anchored right: dragging the left edge further left grows the panel.
        setWidth(clampChatWidth(window.innerWidth - event.clientX));
    };

    const handleResizeEnd = (event: React.PointerEvent<HTMLDivElement>): void => {
        if (!resizingRef.current) {
            return;
        }
        resizingRef.current = false;
        event.currentTarget.releasePointerCapture(event.pointerId);
        try {
            localStorage.setItem(CHAT_WIDTH_KEY, String(width));
        } catch {
            // ignore — storage disabled
        }
    };

    let headerTitle = translate('chat_title');
    if (showIntro) {
        headerTitle = translate('chat_intro_header');
    } else if (showSessions) {
        headerTitle = translate('chat_history');
    }

    let bodyContent: React.ReactNode;
    if (showIntro) {
        bodyContent = (
            <Box
                sx={{
                    flexGrow: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 3,
                    py: 3,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Typography variant='h6'>{translate('chat_intro_title')}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {translate('chat_intro_body_1')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {translate('chat_intro_body_2')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                        {translate('chat_intro_body_3')}
                    </Typography>
                </Stack>
                {!hasConsented && (
                    <FormControlLabel
                        sx={{ mt: 2, alignItems: 'flex-start' }}
                        control={
                            <Checkbox
                                checked={consentChecked}
                                onChange={event => setConsentChecked(event.target.checked)}
                                sx={{ pt: 0 }}
                            />
                        }
                        label={
                            <Typography variant='body2' color='text.secondary'>
                                {translate('chat_consent_label')}
                            </Typography>
                        }
                    />
                )}
                <LoadingButton
                    fullWidth
                    variant='contained'
                    loading={loading || savingConsent}
                    disabled={!hasConsented && !consentChecked}
                    onClick={handleContinueFromIntro}
                    sx={{ mt: hasConsented ? 2 : 1 }}
                >
                    {translate('chat_intro_continue')}
                </LoadingButton>
            </Box>
        );
    } else if (showSessions) {
        bodyContent = (
            <ChatSessionList
                sessions={sessions}
                activeSessionId={activeSessionId}
                busy={loading}
                onSelect={handleSelectSession}
                onNew={handleRequestNewChat}
                onRequestDelete={setDeleteTargetIds}
            />
        );
    } else {
        bodyContent = (
            <Box ref={messagesContainerRef} sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2 }}>
                <Stack spacing={1.5}>
                    {historyLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} aria-label={translate('chat_history_loading')} />
                        </Box>
                    )}
                    {messages.map((message, index) => (
                        <Stack
                            key={index}
                            alignItems={message.role === 'user' ? 'flex-end' : 'flex-start'}
                            sx={{ alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}
                        >
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    bgcolor: message.role === 'user' ? 'primary.main' : 'grey.200',
                                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                }}
                            >
                                <Typography variant='body2' component='div' sx={{ wordBreak: 'break-word' }}>
                                    <ChatRichText text={message.content} />
                                </Typography>
                            </Box>
                            {message.messageId !== undefined && (
                                <ChatMessageRating
                                    rating={message.rating}
                                    disabled={loading}
                                    onRate={rating => handleRate(message.messageId as number, rating)}
                                />
                            )}
                        </Stack>
                    ))}
                    {showScope && (
                        <Box sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                            <Typography variant='caption' color='text.secondary'>
                                {translate('chat_scope_title')}
                            </Typography>
                            {scopeChips}
                        </Box>
                    )}
                    {/* Three bouncing dots, staggered by animation-delay, while waiting for the
                        first character — replaced by the self-writing bubble below the moment
                        text actually starts arriving. `role='status'` + the visually-hidden label
                        keep this announced to screen readers as "typing", not as three blank dots. */}
                    {loading && !streamingText && (
                        <Box
                            role='status'
                            sx={{
                                alignSelf: 'flex-start',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 1.25,
                                borderRadius: 1.5,
                                bgcolor: 'grey.200',
                            }}
                        >
                            <Box component='span' sx={visuallyHidden}>
                                {translate('chat_typing')}
                            </Box>
                            {[0, 1, 2].map(dotIndex => (
                                <Box
                                    key={dotIndex}
                                    aria-hidden
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: 'text.secondary',
                                        '@keyframes chatTypingBounce': {
                                            '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
                                            '40%': { transform: 'scale(1)', opacity: 1 },
                                        },
                                        animation: 'chatTypingBounce 1.2s ease-in-out infinite',
                                        animationDelay: `${dotIndex * 0.15}s`,
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                    {/* The self-writing animation: revealTextOverTime paces this onto the
                        screen letter by letter on its own clock (see that function), replacing
                        the plain "typing" indicator above the moment the first characters land.
                        Not a stored message — it only becomes one once the turn completes and
                        the animation has fully caught up (see handleSend's success path). */}
                    {loading && streamingText && (
                        <Box
                            sx={{
                                alignSelf: 'flex-start',
                                maxWidth: '85%',
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: 'grey.200',
                                color: 'text.primary',
                            }}
                        >
                            <Typography variant='body2' component='div' sx={{ wordBreak: 'break-word' }}>
                                <ChatRichText text={streamingText} />
                                <Box
                                    component='span'
                                    aria-hidden
                                    sx={{
                                        display: 'inline-block',
                                        width: '2px',
                                        height: '1em',
                                        ml: '2px',
                                        verticalAlign: 'text-bottom',
                                        bgcolor: 'text.primary',
                                        '@keyframes chatCursorBlink': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0 },
                                        },
                                        animation: 'chatCursorBlink 0.9s step-start infinite',
                                    }}
                                />
                            </Typography>
                        </Box>
                    )}
                    {loading && searchingDocs && (
                        <Stack
                            role='status'
                            direction='row'
                            alignItems='center'
                            spacing={1}
                            sx={{ alignSelf: 'flex-start', px: 1.5, color: 'text.secondary' }}
                        >
                            <CircularProgress size={14} color='inherit' />
                            <Typography variant='caption'>{translate('chat_searching_docs')}</Typography>
                        </Stack>
                    )}
                </Stack>
            </Box>
        );
    }

    return (
        <Drawer
            anchor='right'
            open={open}
            onClose={onClose}
            // PageZoom (the A+/A- accessibility widget) is fixed top-right at zIndex 1201;
            // the drawer defaults to zIndex.drawer (1200), so lift it above to avoid overlap.
            sx={{ zIndex: theme => theme.zIndex.drawer + 2 }}
            PaperProps={{
                // No explicit `position` here — MUI's own Drawer paper classes
                // already set `position: fixed` (anchoring it flush to the
                // right edge), and that's already a valid positioning context
                // for the absolutely-positioned resize handle below. Adding
                // `position: 'relative'` here previously overrode that fixed
                // positioning and made the drawer jump on open.
                sx: {
                    width: { xs: '100%', sm: `${width}px` },
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {isResizable && (
                <Box
                    onPointerDown={handleResizeStart}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                    // Straddles the drawer's left edge so it's easy to grab without
                    // eating into the content's own padding.
                    sx={{
                        position: 'absolute',
                        left: -3,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        cursor: 'col-resize',
                        touchAction: 'none',
                        zIndex: 1,
                        '&:hover': { bgcolor: 'divider' },
                    }}
                />
            )}
            <Stack
                direction='row'
                alignItems='center'
                justifyContent='space-between'
                sx={{ px: 2, py: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
            >
                <Typography variant='subtitle1'>{headerTitle}</Typography>
                <Stack direction='row' alignItems='center'>
                    {showIntro ? (
                        // Nothing to go back to on the very first ever open — only
                        // offer the back arrow once there's a conversation list behind it.
                        sessions.length > 0 && (
                            <IconButton
                                onClick={handleCancelIntro}
                                disabled={loading}
                                aria-label={translate('chat_back_to_conversation')}
                            >
                                <Icon icon={backIcon} width={22} height={22} />
                            </IconButton>
                        )
                    ) : (
                        <>
                            {!showSessions && (
                                <IconButton
                                    onClick={() => setHandoffOpen(true)}
                                    disabled={loading || !hasAskedSomething}
                                    aria-label={translate('chat_handoff_open')}
                                    title={translate('chat_handoff_open')}
                                >
                                    <Icon icon={supportAgentIcon} width={22} height={22} />
                                </IconButton>
                            )}
                            <IconButton
                                onClick={() => setShowSessions(value => !value)}
                                // Disabled mid-turn so a reply can't land against a session
                                // the user has meanwhile switched away from.
                                disabled={loading}
                                aria-label={
                                    showSessions ? translate('chat_back_to_conversation') : translate('chat_history')
                                }
                            >
                                <Icon icon={showSessions ? backIcon : historyIcon} width={22} height={22} />
                            </IconButton>
                        </>
                    )}
                    <IconButton onClick={onClose} aria-label='close chat'>
                        <Icon icon={closeIcon} width={22} height={22} />
                    </IconButton>
                </Stack>
            </Stack>

            {canTestAs && <ChatTestAsBar disabled={loading} onChange={handleTestAsChange} />}

            {bodyContent}

            {!showSessions && !showIntro && (
                <Box sx={{ p: 2, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
                    {/* The module chips again, once the greeting's copy has scrolled away. */}
                    {modulesInScope.length > 0 && !showScope && (
                        <Box sx={{ mb: 1 }}>
                            <Button
                                size='small'
                                color='inherit'
                                onClick={() => setTopicsOpen(current => !current)}
                                endIcon={
                                    <Icon
                                        icon={expandMoreIcon}
                                        style={{
                                            transform: topicsOpen ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 200ms',
                                        }}
                                    />
                                }
                                aria-expanded={topicsOpen}
                                sx={{ color: 'text.secondary', px: 1 }}
                            >
                                {translate('chat_scope_toggle')}
                            </Button>
                            <Collapse in={topicsOpen} timeout={200} unmountOnExit>
                                <Box sx={{ maxHeight: 120, overflowY: 'auto' }}>{scopeChips}</Box>
                            </Collapse>
                        </Box>
                    )}
                    <Stack direction='row' spacing={1} alignItems='flex-end'>
                        <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            size='small'
                            value={input}
                            onChange={event => setInput(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={translate(outOfMessages ? 'chat_quota_used_up' : 'chat_placeholder')}
                            InputProps={{
                                endAdornment: quotaBadge,
                            }}
                        />
                        <LoadingButton
                            variant='contained'
                            loading={loading}
                            disabled={!input.trim() || !activeSessionId || (outOfMessages && !isChatNote(input))}
                            onClick={() => handleSend()}
                            sx={{ minWidth: 0, px: 1.5 }}
                            aria-label='send message'
                        >
                            <Icon icon={sendIcon} width={22} height={22} />
                        </LoadingButton>
                    </Stack>
                </Box>
            )}

            <ChatHandoffDialog
                open={handoffOpen}
                sessionId={activeSessionId}
                testAs={testAs}
                onClose={() => setHandoffOpen(false)}
            />

            <Dialog open={!!deleteTargetIds} onClose={() => setDeleteTargetIds(null)} maxWidth='xs' fullWidth>
                <DialogTitle>
                    {deleteTargetIds && deleteTargetIds.length > 1
                        ? translate('chat_delete_many_title', { n: deleteTargetIds.length })
                        : translate('chat_delete_title')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                        {translate(
                            deleteTargetIds && deleteTargetIds.length > 1
                                ? 'chat_delete_many_body'
                                : 'chat_delete_body',
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                    <LoadingButton variant='outlined' onClick={() => setDeleteTargetIds(null)}>
                        {translate('cancel')}
                    </LoadingButton>
                    <LoadingButton color='error' variant='contained' onClick={handleConfirmDelete}>
                        {translate('chat_delete_confirm')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default ChatPanel;
